import { useEffect, useState } from 'react';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';

type Announcement = {
  title: string;
  excerpt?: string;
};

/**
 * Fetches the latest active announcement and renders a styled banner.
 *
 * Ported from the legacy app's Root.tsx CustomAnnouncementBanner. Renders
 * nothing while loading or when there is no active announcement.
 */
export const AnnouncementBanner = () => {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState(true);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  useEffect(() => {
    const fetchLatestAnnouncement = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('announcements');
        const response = await fetchApi.fetch(
          `${baseUrl}/announcements?active=true&max=1`,
        );

        if (response.ok) {
          const data = await response.json();
          if (data.results && data.results.length > 0) {
            setAnnouncement(data.results[0]);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch announcements:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestAnnouncement();
  }, [discoveryApi, fetchApi]);

  // Don't render anything if no announcement or still loading
  if (loading || !announcement) {
    return null;
  }

  return (
    <div
      style={{
        backgroundColor: '#e3f2fd',
        border: '1px solid #2196f3',
        borderRadius: '4px',
        padding: '12px 16px',
        margin: 0,
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      <span>📢</span>
      <div style={{ flex: 1 }}>
        <strong>{announcement.title}</strong>
        {announcement.excerpt && (
          <span style={{ marginLeft: '8px' }}>- {announcement.excerpt}</span>
        )}
      </div>
    </div>
  );
};
